import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Seo } from "../../../components/Seo";
import { CimmplePayMarkdown, splitLeadingH1 } from "../../../components/docs/CimmplePayMarkdown";
import {
  cimmplePayDocsByRoute,
  cimmplePaySidebarGroups,
  cimmplePaySidebarReferenceFolders,
  type CimmplePayDocItem,
} from "../../../content/cimmplepay/manifest";
import { cimmplePayInlineDocs } from "../../../content/cimmplepay/inlineDocs";

const MISSING_DOC = `# Document Not Found

The requested documentation page was not found in the imported CimmplePay docs set.
`;

function withPublicUrl(path: string) {
  const base = process.env.PUBLIC_URL ?? "";
  const normalizedBase = base.endsWith("/") ? base.slice(0, -1) : base;
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
}

function getDocForRoute(routeSlug: string): CimmplePayDocItem | undefined {
  return cimmplePayDocsByRoute.get(routeSlug);
}

function renderNavLink(item: CimmplePayDocItem, routeSlug: string) {
  const to = item.routePath ? `/docs/cimmplepay/${item.routePath}` : "/docs/cimmplepay";
  const isActive = routeSlug === item.routePath;
  return (
    <li key={item.sourcePath}>
      <Link
        to={to}
        className={`block rounded-lg px-3 py-2 text-sm transition-colors ${
          isActive
            ? "bg-primary-50 text-primary-700 dark:bg-dark-800/60 dark:text-white"
            : "text-slate-700 dark:text-dark-300 hover:bg-slate-100 dark:hover:bg-dark-800/40"
        }`}
      >
        {item.title}
      </Link>
    </li>
  );
}

const CimmplePayDocsExplorer: React.FC = () => {
  const params = useParams();
  const routeSlug = (params["*"] ?? "").replace(/^\/+|\/+$/g, "");
  const docItem = getDocForRoute(routeSlug);
  const [markdown, setMarkdown] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setIsLoading(true);
      if (!docItem) {
        if (isMounted) {
          setMarkdown(MISSING_DOC);
          setIsLoading(false);
        }
        return;
      }

      try {
        const sourcePath = docItem.sourcePath
          .split("/")
          .map((segment) => encodeURIComponent(segment))
          .join("/");
        const res = await fetch(withPublicUrl(`/content/cimmplepay/${sourcePath}`));
        if (!res.ok) throw new Error(`Failed to load ${docItem.sourcePath}`);
        const text = await res.text();
        if (isMounted) setMarkdown(text);
      } catch {
        if (isMounted) {
          const fallback = cimmplePayInlineDocs[docItem.sourcePath];
          setMarkdown(fallback ?? MISSING_DOC);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [docItem]);

  const { title, body } = useMemo(() => splitLeadingH1(markdown), [markdown]);
  const seoTitle = `${docItem?.title ?? "CimmplePay Docs"} | Cimmple`;
  const seoDescription = `CimmplePay documentation: ${docItem?.title ?? "integration documentation"}.`;

  return (
    <div className="pt-24">
      <Seo title={seoTitle} description={seoDescription} />

      <section className="pt-8 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <aside className="lg:col-span-1">
              <div className="card p-5 sticky top-28">
                <h2 className="text-lg font-display font-bold text-slate-900 dark:text-white mb-4">
                  CimmplePay Docs
                </h2>
                <nav className="space-y-5 max-h-[70vh] overflow-y-auto pr-2">
                  {cimmplePaySidebarGroups.map((group) => (
                    <div key={group.label}>
                      <h3 className="text-xs uppercase tracking-wide font-semibold text-slate-500 dark:text-dark-400 mb-2">
                        {group.label}
                      </h3>
                      <ul className="space-y-1.5">
                        {group.items.map((item) => renderNavLink(item, routeSlug))}
                      </ul>
                    </div>
                  ))}
                  <div>
                    <h3 className="text-xs uppercase tracking-wide font-semibold text-slate-500 dark:text-dark-400 mb-2">
                      Reference
                    </h3>
                    <div className="space-y-4">
                      {cimmplePaySidebarReferenceFolders.map((refGroup) => (
                        <div key={refGroup.folder || "overview"}>
                          <h4 className="text-[11px] font-semibold text-slate-600 dark:text-dark-400 mb-1.5 pl-1">
                            {refGroup.label}
                          </h4>
                          <ul className="space-y-1.5 border-l border-slate-200 dark:border-dark-700 pl-2 ml-1">
                            {refGroup.items.map((item) => renderNavLink(item, routeSlug))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                </nav>
              </div>
            </aside>

            <div className="lg:col-span-3">
              <div className="card p-8 md:p-12">
                {isLoading ? (
                  <p className="text-slate-600 dark:text-dark-300">Loading documentation...</p>
                ) : (
                  <>
                    {title ? (
                      <h1 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-6">
                        {title}
                      </h1>
                    ) : null}
                    <CimmplePayMarkdown markdown={body} />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CimmplePayDocsExplorer;
